import React, { useState } from 'react';
import axios from 'axios';
import './PerformanceDashboard.css';

const PerformanceDashboard = ({ metrics, userId, onRefresh }) => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicDetails, setTopicDetails] = useState(null);

  const fetchTopicDetails = async (topic, grade) => {
    try {
      const response = await axios.get(
        `/api/metrics/topic/${userId}/${topic}?grade=${grade}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setTopicDetails(response.data);
      setSelectedTopic(topic);
    } catch (err) {
      console.error('Error fetching topic details:', err);
    }
  };

  const renderStreakCard = () => {
    const streaks = metrics?.user?.metrics?.streaks || {};
    const current = streaks.current || 0;
    const longest = streaks.longest || 0;
    const streakEmoji = current >= 7 ? '🔥' : current >= 3 ? '✨' : '📚';
    
    return (
      <div className="stat-card streak-card">
        <div className="stat-icon">{streakEmoji}</div>
        <div className="stat-content">
          <h3>Current Streak</h3>
          <div className="stat-value">{current} days</div>
          <div className="stat-subtitle">Best: {longest} days</div>
        </div>
      </div>
    );
  };

  const renderAccuracyCard = () => {
    const accuracy = metrics?.user?.metrics?.averageAccuracy || 0;
    const color = accuracy >= 80 ? '#4caf50' : accuracy >= 60 ? '#ff9800' : '#f44336';
    
    return (
      <div className="stat-card accuracy-card">
        <div className="stat-icon">🎯</div>
        <div className="stat-content">
          <h3>Average Accuracy</h3>
          <div className="stat-value" style={{ color }}>{accuracy.toFixed(1)}%</div>
          <div className="stat-subtitle">
            {metrics?.user?.metrics?.correctAnswers || 0} / {metrics?.user?.metrics?.questionsAnswered || 0} correct
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionsCard = () => {
    return (
      <div className="stat-card questions-card">
        <div className="stat-icon">📝</div>
        <div className="stat-content">
          <h3>Questions Answered</h3>
          <div className="stat-value">{metrics?.user?.metrics?.questionsAnswered || 0}</div>
          <div className="stat-subtitle">
            {metrics?.stats?.savedItems || 0} saved for review
          </div>
        </div>
      </div>
    );
  };

  const renderTopicProgress = () => {
    const topics = metrics?.user?.metrics?.topicProgress || [];
    
    // Handle case where there are no topics yet
    if (!topics || topics.length === 0) {
      return (
        <div className="topic-progress-section">
          <h3>Topic Mastery</h3>
          <div className="empty-state">
            <p>No topic progress yet. Start answering questions to track your mastery!</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="topic-progress-section">
        <h3>Topic Mastery</h3>
        <div className="topic-grid">
          {topics.map((topic, index) => (
            <div 
              key={index}
              className="topic-item"
              onClick={() => fetchTopicDetails(topic.topic, topic.grade)}
            >
              <div className="topic-header">
                <span className="topic-name">{topic.topic}</span>
                <span className="topic-grade">Grade {topic.grade}</span>
              </div>
              <div className="mastery-bar">
                <div 
                  className="mastery-fill"
                  style={{ 
                    width: `${topic.mastery || 0}%`,
                    backgroundColor: getMasteryColor(topic.mastery)
                  }}
                ></div>
              </div>
              <div className="topic-stats">
                <span>{topic.questionsAnswered || 0} questions</span>
                <span>{topic.mastery || 0}% mastery</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeeklyChart = () => {
    // weeklyPerformance is now an OBJECT with summary data, not an array
    // Structure: { questionsAnswered, correctAnswers, accuracy, topicsStudied, averageTimePerQuestion }
    const weeklyPerformance = metrics?.stats?.weeklyPerformance;
    
    // If weeklyPerformance is an array (old format), use it directly
    // If it's an object (new format), create a summary display
    if (Array.isArray(weeklyPerformance) && weeklyPerformance.length > 0) {
      // Old array format - render as chart bars
      const maxQuestions = Math.max(...weeklyPerformance.map(d => d.questions || 0), 10);
      
      return (
        <div className="weekly-chart">
          <h3>This Week's Activity</h3>
          <div className="chart-container">
            {weeklyPerformance.map((day, index) => {
              const date = new Date(day.date);
              const dayName = date.toLocaleDateString('en', { weekday: 'short' });
              const height = ((day.questions || 0) / maxQuestions) * 100;
              
              return (
                <div key={index} className="chart-bar-container">
                  <div className="chart-bar-wrapper">
                    <div 
                      className="chart-bar"
                      style={{ 
                        height: `${height}%`,
                        backgroundColor: (day.accuracy || 0) >= 70 ? '#4caf50' : '#ff9800'
                      }}
                    >
                      <span className="bar-value">{day.questions || 0}</span>
                    </div>
                  </div>
                  <div className="chart-label">{dayName}</div>
                  <div className="chart-accuracy">{day.accuracy || 0}%</div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    // New object format - render as summary cards
    const summary = weeklyPerformance || {};
    const questionsAnswered = summary.questionsAnswered || 0;
    const correctAnswers = summary.correctAnswers || 0;
    const accuracy = summary.accuracy || 0;
    const topicsStudied = Array.isArray(summary.topicsStudied) ? summary.topicsStudied : [];
    const avgTime = summary.averageTimePerQuestion || 0;
    
    return (
      <div className="weekly-chart">
        <h3>This Week's Activity</h3>
        <div className="weekly-summary">
          <div className="summary-stat">
            <div className="summary-value">{questionsAnswered}</div>
            <div className="summary-label">Questions</div>
          </div>
          <div className="summary-stat">
            <div className="summary-value">{correctAnswers}</div>
            <div className="summary-label">Correct</div>
          </div>
          <div className="summary-stat">
            <div className="summary-value" style={{ 
              color: accuracy >= 70 ? '#4caf50' : accuracy >= 50 ? '#ff9800' : '#f44336' 
            }}>
              {accuracy}%
            </div>
            <div className="summary-label">Accuracy</div>
          </div>
          <div className="summary-stat">
            <div className="summary-value">{topicsStudied.length}</div>
            <div className="summary-label">Topics</div>
          </div>
          {avgTime > 0 && (
            <div className="summary-stat">
              <div className="summary-value">{Math.round(avgTime)}s</div>
              <div className="summary-label">Avg Time</div>
            </div>
          )}
        </div>
        {topicsStudied.length > 0 && (
          <div className="topics-studied">
            <span className="topics-label">Topics studied: </span>
            <span className="topics-list">{topicsStudied.join(', ')}</span>
          </div>
        )}
        {questionsAnswered === 0 && (
          <div className="empty-state">
            <p>No activity this week yet. Start practicing to see your progress!</p>
          </div>
        )}
      </div>
    );
  };

  const renderTopicDetails = () => {
    if (!selectedTopic || !topicDetails) return null;
    
    return (
      <div className="topic-details-modal">
        <div className="topic-details-content">
          <button className="close-btn" onClick={() => setSelectedTopic(null)}>×</button>
          <h3>{selectedTopic} Details</h3>
          
          <div className="detail-stats">
            <div className="detail-stat">
              <span className="label">Mastery Level:</span>
              <span className="value">{topicDetails.masteryLevel || 'Beginner'}</span>
            </div>
            <div className="detail-stat">
              <span className="label">Trend:</span>
              <span className={`value trend-${topicDetails.trends?.trend || 'neutral'}`}>
                {topicDetails.trends?.trend || 'Neutral'}
                {topicDetails.trends?.improvement !== 0 && (
                  <span> ({topicDetails.trends?.improvement > 0 ? '+' : ''}{topicDetails.trends?.improvement}%)</span>
                )}
              </span>
            </div>
          </div>
          
          {topicDetails.recentQuestions?.length > 0 && (
            <div className="recent-questions">
              <h4>Recent Questions</h4>
              {topicDetails.recentQuestions.slice(0, 5).map((q, index) => (
                <div key={index} className={`question-item ${q.correct ? 'correct' : 'incorrect'}`}>
                  <span className="status-icon">{q.correct ? '✓' : '✗'}</span>
                  <span className="difficulty">{q.difficulty}</span>
                  <span className="date">{new Date(q.answeredAt).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const getMasteryColor = (mastery) => {
    if (mastery >= 80) return '#4caf50';
    if (mastery >= 60) return '#2196f3';
    if (mastery >= 40) return '#ff9800';
    return '#f44336';
  };

  if (!metrics) {
    return (
      <div className="performance-dashboard empty">
        <p>No metrics data available</p>
        {onRefresh && (
          <button onClick={onRefresh} className="refresh-btn">
            Refresh Data
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="performance-dashboard">
      <div className="stats-grid">
        {renderStreakCard()}
        {renderAccuracyCard()}
        {renderQuestionsCard()}
      </div>
      
      {renderWeeklyChart()}
      {renderTopicProgress()}
      
      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {metrics?.stats?.recentActivity ? (
            <>
              {(metrics.stats.recentActivity.questions || []).map((q, index) => (
                <div key={`q-${index}`} className="activity-item">
                  <span className="activity-icon">{q.correct ? '✅' : '📝'}</span>
                  <span className="activity-text">
                    {q.correct ? 'Correctly answered' : 'Attempted'} {q.topic} question
                  </span>
                  <span className="activity-time">
                    {q.date ? new Date(q.date).toLocaleDateString() : 'Recently'}
                  </span>
                </div>
              ))}
              {(metrics.stats.recentActivity.posts || []).map((p, index) => (
                <div key={`p-${index}`} className="activity-item">
                  <span className="activity-icon">💬</span>
                  <span className="activity-text">{p.content}...</span>
                  <span className="activity-time">
                    {p.date ? new Date(p.date).toLocaleDateString() : 'Recently'}
                  </span>
                </div>
              ))}
              {(!metrics.stats.recentActivity.questions?.length && 
                !metrics.stats.recentActivity.posts?.length) && (
                <div className="empty-state">
                  <p>No recent activity. Start learning to see your progress here!</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p>No recent activity to display.</p>
            </div>
          )}
        </div>
      </div>
      
      {renderTopicDetails()}
    </div>
  );
};

export default PerformanceDashboard;